import { useState, useEffect } from "react";
import styles from "./CreateCommunityPage.module.css";
import placeholderImage from "../../assets/images/image-upload-placeholder.png";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import useDashboardStore from "../../store/dashboardStore";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";

const CreateCommunityPage = () => {
  const { setIsAllCommunityScreen, editCommunityId } = useDashboardStore();

  const [communityName, setCommunityName] = useState("");
  const [themeColor, setThemeColor] = useState("#3b0a59");
  const [hasLiveSession, setHasLiveSession] = useState(false);
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [newSection, setNewSection] = useState("");
  const [sections, setSections] = useState([]);
  const [searchText, setSearchText] = useState("");

  const [bannerFile, setBannerFile] = useState(null);
  const [iconFile, setIconFile] = useState(null);

  const [bannerPreview, setBannerPreview] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreatingSection, setIsCreatingSection] = useState(false);

  // Rename functionality states
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState("");
  const [isRenamingSection, setIsRenamingSection] = useState(false);

  // Delete functionality states
  const [deletingSectionId, setDeletingSectionId] = useState(null);

  // COMMUNITY CONTENT STATE
  const [content, setContent] = useState({
    communityTitle: "",
    contentTitle: "",
    primaryText: "",
    secondaryText: "",

    contentBannerFile: null,
    contentBannerUrl: "",

    contentImageFile: null,
    contentImageUrl: "",

    deleteContentBanner: false,
    deleteContentImage: false,
  });

  // VIDEOS LINK STATE
  const [memberships, setMemberships] = useState([]);
  const [searchTextMembership, setSearchTextMembership] = useState("");
  const [videoLinks, setVideoLinks] = useState([]);
  const [editVideoIndex, setEditVideoIndex] = useState(-1);
  const [isVideoFree, setIsVideoFree] = useState(true);

  const [currentVideo, setCurrentVideo] = useState({
    title: "",
    url: "",
    videoType: "youtube",
    membershipIds: [],
    sortOrder: 0,
  });

  const resetVideoForm = () => {
    setCurrentVideo({
      title: "",
      url: "",
      videoType: "youtube",
      membershipIds: [],
      sortOrder: 0,
    });
    setIsVideoFree(true);
    setEditVideoIndex(-1);
  };
  const handleAddOrUpdateVideo = () => {
    if (!currentVideo.url.trim()) {
      toast.error("Please enter a video URL");
      return;
    }

    // Fix: Safely check membershipIds with optional chaining and default to empty array
    const membershipIds = currentVideo.membershipIds || [];

    if (!isVideoFree && membershipIds.length === 0) {
      toast.error("Please select membership for paid video");
      return;
    }

    // Auto-detect video type
    let detectedType = "other";
    const url = currentVideo.url.trim();
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      detectedType = "youtube";
    } else if (url.includes("vimeo.com")) {
      detectedType = "vimeo";
    }

    const payload = {
      title: currentVideo.title?.trim() || null,
      url: url,
      videoType: detectedType,
      membershipIds: isVideoFree ? [] : membershipIds,
      sort_order:
        editVideoIndex === -1 ? videoLinks.length : currentVideo.sortOrder,
    };

    if (editVideoIndex === -1) {
      // Adding new video
      setVideoLinks((prev) => [...prev, payload]);
    } else {
      // Updating existing video
      setVideoLinks((prev) =>
        prev.map((v, i) => (i === editVideoIndex ? payload : v))
      );
    }

    resetVideoForm();
  };

  const handleEditVideo = (index) => {
    const video = videoLinks[index];
    setCurrentVideo({
      ...video,
      membershipIds: video.membershipIds || [],
      sortOrder: video.sort_order || index,
    });
    setIsVideoFree((video.membershipIds || []).length === 0);
    setEditVideoIndex(index);
  };

  const handleDeleteVideo = (index) => {
    setVideoLinks((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Reassign sort orders after deletion
      return updated.map((video, i) => ({
        ...video,
        sort_order: i,
      }));
    });
  };

  const handleMoveVideoUp = (index) => {
    if (index === 0) return;

    setVideoLinks((prev) => {
      const newList = [...prev];
      [newList[index - 1], newList[index]] = [
        newList[index],
        newList[index - 1],
      ];

      // Update sort orders
      return newList.map((video, i) => ({
        ...video,
        sort_order: i,
      }));
    });
  };

  const handleMoveVideoDown = (index) => {
    if (index === videoLinks.length - 1) return;

    setVideoLinks((prev) => {
      const newList = [...prev];
      [newList[index], newList[index + 1]] = [
        newList[index + 1],
        newList[index],
      ];

      // Update sort orders
      return newList.map((video, i) => ({
        ...video,
        sort_order: i,
      }));
    });
  };

  const handleContentBannerUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setContent((prev) => ({
      ...prev,
      contentBannerFile: file,
      contentBannerUrl: URL.createObjectURL(file),
      deleteContentBanner: false,
    }));
  };

  const handleContentImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setContent((prev) => ({
      ...prev,
      contentImageFile: file,
      contentImageUrl: URL.createObjectURL(file),
      deleteContentImage: false,
    }));
  };

  const deleteContentBanner = () => {
    setContent((prev) => ({
      ...prev,
      contentBannerFile: null,
      contentBannerUrl: "",
      deleteContentBanner: true,
    }));
  };

  const deleteContentImage = () => {
    setContent((prev) => ({
      ...prev,
      contentImageFile: null,
      contentImageUrl: "",
      deleteContentImage: true,
    }));
  };

  const handleMembershipChange = (membership) => {
    const currentIds = currentVideo.membershipIds || [];
    let ids = currentIds.includes(membership.id)
      ? currentIds.filter((s) => s !== membership.id)
      : [...currentIds, membership.id];
    setCurrentVideo({ ...currentVideo, membershipIds: ids });
  };

  const handleCheckboxChange = (section) => {
    setSelectedSectionIds((prev) =>
      prev.includes(section.id)
        ? prev.filter((s) => s !== section.id)
        : [...prev, section.id]
    );
  };

  const handleBannerUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleIconUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIconFile(file);
      setIconPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!communityName || !themeColor || !selectedSectionIds.length) {
      toast.error("Please fill all fields.");
      return;
    }

    if (editCommunityId === null) {
      if (!bannerFile || !iconFile) {
        toast.error("Please upload both images.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", communityName);
      formData.append("theme_color", themeColor);
      formData.append(
        "has_live_session",
        hasLiveSession === true ? true : false
      );
      formData.append("section_ids", JSON.stringify(selectedSectionIds));
      formData.append("banner", bannerFile);
      formData.append("icon", iconFile);

      if (content.communityTitle)
        formData.append("community_title", content.communityTitle);

      if (content.contentTitle)
        formData.append("content_title", content.contentTitle);

      if (content.primaryText)
        formData.append("content_primary_text", content.primaryText);

      if (content.secondaryText)
        formData.append("content_secondary_text", content.secondaryText);

      if (content.deleteContentBanner)
        formData.append("delete_content_banner", "true");

      if (content.deleteContentImage)
        formData.append("delete_content_image", "true");

      if (content.contentBannerFile)
        formData.append("content_banner", content.contentBannerFile);

      if (content.contentImageFile)
        formData.append("content_image", content.contentImageFile);

      // Videos with proper sort order
      if (videoLinks.length > 0) {
        const sortedVideos = videoLinks.map((v, i) => ({
          ...v,
          sort_order: i,
        }));
        formData.append("video_links", JSON.stringify(sortedVideos));
      }

      const { data } =
        editCommunityId !== null
          ? await api.put(
              ENDPOINTS.UPDATE_COMMUNITY_BY_ID(editCommunityId),
              formData
            )
          : await api.post(ENDPOINTS.CREATE_COMMUNITY, formData);

      if (data.success) {
        toast.success(data.message || "Community created successfully");
        setIsAllCommunityScreen();
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating community");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSection = async () => {
    if (isCreatingSection || !newSection.trim()) return;

    setIsCreatingSection(true);

    try {
      const payload = {
        title: newSection,
      };

      const { data } = await api.post(ENDPOINTS.CREATE_SECTION, payload);
      if (data.success) {
        const res = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
        if (res.data.success) {
          setSections(res.data.sections);
          const newSectionCreated = res.data.sections.find(
            (s) => s.title === newSection
          );
          if (newSectionCreated) {
            handleCheckboxChange(newSectionCreated);
          }
          setNewSection("");
        }
      } else {
        toast.error(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error creating section");
    } finally {
      setIsCreatingSection(false);
    }
  };

  const handleStartEdit = (section) => {
    setEditingSectionId(section.id);
    setEditingSectionTitle(section.title);
  };

  const handleCancelEdit = () => {
    setEditingSectionId(null);
    setEditingSectionTitle("");
  };

  const handleSaveRename = async (sectionId) => {
    if (!editingSectionTitle.trim()) {
      toast.error("Section name cannot be empty");
      return;
    }

    if (isRenamingSection) return;

    setIsRenamingSection(true);

    try {
      const payload = {
        title: editingSectionTitle.trim(),
      };

      const { data } = await api.put(
        ENDPOINTS.UPDATE_SECTION_BY_ID(sectionId),
        payload
      );

      if (data.success) {
        toast.success("Section renamed successfully");

        const res = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
        if (res.data.success) {
          setSections(res.data.sections);
        }

        setEditingSectionId(null);
        setEditingSectionTitle("");
      } else {
        toast.error(data.message || "Failed to rename section");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error renaming section");
    } finally {
      setIsRenamingSection(false);
    }
  };

  const handleDeleteSection = (sectionId) => {
    if (deletingSectionId) return;

    confirmAlert({
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this section?\n\nThis action cannot be undone.",
      buttons: [
        {
          label: "Yes",
          onClick: async () => {
            setDeletingSectionId(sectionId);

            try {
              const { data } = await api.delete(
                ENDPOINTS.DELETE_SECTION_BY_ID(sectionId)
              );

              if (data.success) {
                toast.success("Section deleted successfully");

                setSelectedSectionIds((prev) =>
                  prev.filter((id) => id !== sectionId)
                );

                const res = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
                if (res.data.success) {
                  setSections(res.data.sections);
                }
              } else {
                toast.error(data.message || "Failed to delete section");
              }
            } catch (err) {
              console.error(err);
              const errorMessage =
                err.response?.data?.message ||
                "Error deleting section. This section may be in use by channels or communities.";
              toast.error(errorMessage);
            } finally {
              setDeletingSectionId(null);
            }
          },
        },
        {
          label: "No",
          onClick: () => toast.info("Section deletion cancelled"),
        },
      ],
    });
  };

  useEffect(() => {
    const fetchSections = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_SECTIONS);
        if (data.success) {
          setSections(data.sections);
        }
      } catch (err) {
        console.error("Failed to fetch sections", err);
      }
    };

    const fetchMemberships = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS);
        if (data.success) {
          setMemberships(data.memberships);
        }
      } catch (err) {
        console.error("Failed to fetch memberships", err);
      }
    };

    fetchSections();
    fetchMemberships();
  }, []);

  useEffect(() => {
    const resetForm = () => {
      setCommunityName("");
      setThemeColor("#3b0a59");
      setHasLiveSession(false);
      setSelectedSectionIds([]);
      setBannerPreview(null);
      setIconPreview(null);
      setVideoLinks([]);
    };

    const prefillFormForEdit = async () => {
      const { data } = await api.get(
        ENDPOINTS.GET_COMMUNITY_BY_ID(editCommunityId)
      );
      if (data.success) {
        setCommunityName(data.community.name);
        setThemeColor(data.community.theme_color);
        setHasLiveSession(data.community.has_live_session === 1 ? true : false);
        setSelectedSectionIds(data.community.section_ids);
        let banner = `${ENDPOINTS.IMAGE_BASE_URL}${data.community.banner}`;
        let icon = `${ENDPOINTS.IMAGE_BASE_URL}${data.community.icon}`;
        setBannerPreview(banner);
        setIconPreview(icon);
        setContent({
          communityTitle: data.community.community_title ?? "",
          contentTitle: data.community.content_title ?? "",
          primaryText: data.community.content_primary_text ?? "",
          secondaryText: data.community.content_secondary_text ?? "",

          contentBannerFile: null,
          contentBannerUrl: data.community.content_banner
            ? ENDPOINTS.IMAGE_BASE_URL + data.community.content_banner
            : "",

          contentImageFile: null,
          contentImageUrl: data.community.content_image
            ? ENDPOINTS.IMAGE_BASE_URL + data.community.content_image
            : "",

          deleteContentBanner: false,
          deleteContentImage: false,
        });

        // Sort videos by sort_order when loading
        const videos = data.community.video_links || [];
        const sortedVideos = [...videos].sort(
          (a, b) => (a.sort_order || 0) - (b.sort_order || 0)
        );
        setVideoLinks(sortedVideos);
      }
    };

    if (editCommunityId !== null) {
      prefillFormForEdit();
    } else if (editCommunityId === null) {
      resetForm();
    }
  }, [editCommunityId]);

  const filteredSections = sections.filter((section) =>
    section.title.toLowerCase().includes(searchText.toLowerCase())
  );

  const filteredMemberships = memberships.filter((membership) =>
    membership.label.toLowerCase().includes(searchTextMembership.toLowerCase())
  );

  return (
    <div className={styles.pageWrapper}>
      {/* App Bar */}
      <div className={styles.appbarContainer}>
        <h2 className={styles.appbarTitle}>
          {editCommunityId === null ? "Create" : "Update"} Community
        </h2>
      </div>

      <div className={styles.bodyContainer}>
        {/* Left Section */}
        <div className={styles.leftSection}>
          {/* Image Upload */}
          <div className={styles.imageSectionWrapper}>
            {bannerPreview && (
              <div className={styles.featuredImageBox}>
                <img
                  src={bannerPreview}
                  alt="Banner Preview"
                  className={styles.featuredImageBox}
                />
                <div className={styles.iconUploadContainer}>
                  <img
                    src={iconPreview || placeholderImage}
                    alt="Icon Preview"
                    className={styles.iconPlaceholder}
                  />
                  <div className={styles.uploadLinks}>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose Featured
                      Image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleBannerUpload}
                      />
                    </label>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose icon
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleIconUpload}
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}
            {!bannerPreview && (
              <div className={styles.featuredImageBox}>
                <div className={styles.iconUploadContainer}>
                  <img
                    src={iconPreview || placeholderImage}
                    alt="Icon Preview"
                    className={styles.iconPlaceholder}
                  />
                  <div className={styles.uploadLinks}>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose Featured
                      Image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleBannerUpload}
                      />
                    </label>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose icon
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleIconUpload}
                      />
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Community name */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Community Name</label>
            <input
              type="text"
              value={communityName}
              onChange={(e) => setCommunityName(e.target.value)}
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>

          {/* Community Theme */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Community theme</label>
            <div className={styles.colorPickerWrapper}>
              <input
                type="color"
                value={themeColor}
                onChange={(e) => setThemeColor(e.target.value)}
                className={styles.colorInput}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Live Sessions */}
          <div className={styles.liveSessionRow}>
            <label className={styles.inlineLabel}>Includes Live Sessions</label>

            <div className={styles.toggleWrapper}>
              <button
                className={`${styles.toggleOption} ${
                  hasLiveSession ? styles.activeToggle : ""
                }`}
                onClick={() => setHasLiveSession(true)}
                disabled={isSubmitting}
              >
                Yes
              </button>
              <button
                className={`${styles.toggleOption} ${
                  !hasLiveSession ? styles.activeToggle : ""
                }`}
                onClick={() => setHasLiveSession(false)}
                disabled={isSubmitting}
              >
                No
              </button>
            </div>
            <span className={styles.note}>
              <span className={styles.noteHighlight}>NB*</span>: This option
              will create a new section page for
              <br />
              live sessions in this community
            </span>
          </div>

          {/* Sections Card */}
          <div className={styles.sectionsWrapper}>
            <label className={styles.inlineLabel}>Sections List</label>
            <div className={styles.sectionsCard}>
              <div className={styles.searchBar}>
                <input
                  type="text"
                  placeholder="type in to Filter sections"
                  className={styles.searchInput}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  disabled={isSubmitting}
                />
                <span className={styles.searchIcon}>🔍</span>
              </div>

              <div className={styles.sectionList}>
                {filteredSections.map((section) => (
                  <div className={styles.sectionRow} key={section.id}>
                    {editingSectionId === section.id ? (
                      <div className={styles.editSectionContainer}>
                        <input
                          type="text"
                          value={editingSectionTitle}
                          onChange={(e) =>
                            setEditingSectionTitle(e.target.value)
                          }
                          className={styles.editSectionInput}
                          disabled={isRenamingSection}
                          autoFocus
                        />
                        <div className={styles.editActions}>
                          <button
                            className={styles.saveButton}
                            onClick={() => handleSaveRename(section.id)}
                            disabled={
                              isRenamingSection || !editingSectionTitle.trim()
                            }
                          >
                            {isRenamingSection ? "⟳" : "✓"}
                          </button>
                          <button
                            className={styles.cancelButton}
                            onClick={handleCancelEdit}
                            disabled={isRenamingSection}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className={styles.sectionLabel}>
                          {section.title}
                        </span>
                        <div className={styles.sectionActions}>
                          <button
                            className={styles.editIconButton}
                            onClick={() => handleStartEdit(section)}
                            disabled={
                              isSubmitting || deletingSectionId === section.id
                            }
                            title="Rename section"
                          >
                            ✎
                          </button>
                          <button
                            className={styles.deleteIconButton}
                            onClick={() => handleDeleteSection(section.id)}
                            disabled={
                              isSubmitting || deletingSectionId === section.id
                            }
                            title="Delete section"
                          >
                            {deletingSectionId === section.id ? "⟳" : "🗑"}
                          </button>
                          <input
                            type="checkbox"
                            className={styles.checkbox}
                            checked={selectedSectionIds.includes(section.id)}
                            onChange={() => handleCheckboxChange(section)}
                            disabled={
                              isSubmitting || deletingSectionId === section.id
                            }
                          />
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              <div className={styles.addSection}>
                <input
                  type="text"
                  placeholder="Add new section here"
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                  className={styles.addInputNewSection}
                  disabled={isCreatingSection || isSubmitting}
                />
                <button
                  className={`${styles.addButton} ${
                    isCreatingSection ? styles.loading : ""
                  }`}
                  onClick={handleCreateSection}
                  disabled={
                    isCreatingSection || isSubmitting || !newSection.trim()
                  }
                >
                  {isCreatingSection ? "⟳" : "+"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right Section */}
        <div className={styles.rightSection}>
          <label className={styles.inlineLabel}>
            Community Content (Optional)
          </label>

          {/* Community Title */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Community Title</label>
            <input
              type="text"
              value={content.communityTitle}
              onChange={(e) =>
                setContent({ ...content, communityTitle: e.target.value })
              }
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>

          {/* Content Banner */}
          <div className={styles.imageSectionWrapper}>
            <div className={styles.featuredImageBox}>
              {content.contentBannerUrl && (
                <>
                  <img
                    src={content.contentBannerUrl}
                    alt="Content Banner Image"
                    className={styles.featuredImageBox}
                  />
                  <button
                    type="button"
                    onClick={deleteContentBanner}
                    className={styles.removeImageButton}
                    disabled={isSubmitting}
                    title="Remove image"
                  >
                    ✕
                  </button>
                </>
              )}
              {!content.contentBannerUrl && (
                <div className={styles.iconPlaceholderContainer}>
                  <img
                    src={placeholderImage}
                    alt="Placeholder"
                    className={styles.iconPlaceholder}
                  />
                  <div className={styles.uploadLinks}>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose Content
                      Banner Image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleContentBannerUpload}
                        disabled={isSubmitting}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Content Title */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Content Title</label>
            <input
              type="text"
              value={content.contentTitle}
              onChange={(e) =>
                setContent({ ...content, contentTitle: e.target.value })
              }
              className={styles.textInput}
              disabled={isSubmitting}
            />
          </div>

          {/* Primary Text */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Primary Text</label>
            <textarea
              name="body_text"
              value={content.primaryText}
              onChange={(e) =>
                setContent({ ...content, primaryText: e.target.value })
              }
              className={styles.textAreaLarge}
              rows="4"
              placeholder="Use **text** for bold formatting"
              disabled={isSubmitting}
            />
          </div>
          <span className={styles.hint}>
            Tip: Use **text** to make text bold
          </span>

          {/* Content Image */}
          <div className={styles.imageSectionWrapper}>
            <div className={styles.featuredImageBox}>
              {content.contentImageUrl && (
                <>
                  <img
                    src={content.contentImageUrl}
                    alt="Content Image"
                    className={styles.featuredImageBox}
                  />
                  <button
                    type="button"
                    onClick={deleteContentImage}
                    className={styles.removeImageButton}
                    disabled={isSubmitting}
                    title="Remove image"
                  >
                    ✕
                  </button>
                </>
              )}
              {!content.contentImageUrl && (
                <div className={styles.iconPlaceholderContainer}>
                  <img
                    src={placeholderImage}
                    alt="Placeholder"
                    className={styles.iconPlaceholder}
                  />
                  <div className={styles.uploadLinks}>
                    <label className={styles.uploadLink}>
                      <span className={styles.plusIcon}>+</span> Choose Content
                      Image
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleContentImageUpload}
                        disabled={isSubmitting}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Secondary Text */}
          <div className={styles.rowInputGroup}>
            <label className={styles.inlineLabel}>Secondary Text</label>
            <textarea
              name="body_text"
              value={content.secondaryText}
              onChange={(e) =>
                setContent({ ...content, secondaryText: e.target.value })
              }
              className={styles.textAreaLarge}
              rows="4"
              placeholder="Use **text** for bold formatting"
              disabled={isSubmitting}
            />
          </div>
          <span className={styles.hint}>
            Tip: Use **text** to make text bold
          </span>

          {/* VIDEO LINKS */}
          <div className={styles.sectionsWrapper}>
            <label className={styles.inlineLabel}>Video Links</label>

            <div className={styles.sectionsCard}>
              {/* Video Title */}
              <input
                type="text"
                placeholder="Video title (optional)"
                value={currentVideo.title ?? ""}
                onChange={(e) =>
                  setCurrentVideo({ ...currentVideo, title: e.target.value })
                }
                className={styles.textInput}
                disabled={isSubmitting}
              />

              {/* Video URL */}
              <input
                type="text"
                placeholder="Video URL"
                value={currentVideo.url}
                onChange={(e) =>
                  setCurrentVideo({ ...currentVideo, url: e.target.value })
                }
                className={styles.textInput}
                disabled={isSubmitting}
              />

              {/* Free / Paid Toggle */}
              <div className={styles.liveSessionRow}>
                <div className={styles.toggleWrapper}>
                  <button
                    className={`${styles.toggleOption} ${
                      isVideoFree ? styles.activeToggle : ""
                    }`}
                    onClick={() => setIsVideoFree(true)}
                    disabled={isSubmitting}
                  >
                    Free
                  </button>
                  <button
                    className={`${styles.toggleOption} ${
                      !isVideoFree ? styles.activeToggle : ""
                    }`}
                    onClick={() => setIsVideoFree(false)}
                    disabled={isSubmitting}
                  >
                    Paid
                  </button>
                </div>
              </div>

              {/* Select Memberships */}
              {!isVideoFree && (
                <div className={styles.sectionsCard}>
                  <div className={styles.searchBar}>
                    <input
                      type="text"
                      placeholder="type in to Filter memberships"
                      className={styles.searchInput}
                      value={searchTextMembership}
                      onChange={(e) => setSearchTextMembership(e.target.value)}
                      disabled={isSubmitting}
                    />
                    <span className={styles.searchIcon}>🔍</span>
                  </div>

                  <div className={styles.sectionList}>
                    {filteredMemberships.map((membership, i) => (
                      <div className={styles.sectionRow} key={i}>
                        <span className={styles.sectionLabel}>
                          {membership.label}
                        </span>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={(currentVideo.membershipIds || []).includes(
                            membership.id
                          )}
                          disabled={isSubmitting}
                          onChange={() => handleMembershipChange(membership)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add / Update Button */}
              <button
                className={styles.submitButton}
                onClick={handleAddOrUpdateVideo}
                disabled={isSubmitting}
              >
                <span>
                  {editVideoIndex === -1 ? "Add Video" : "Update Video"}
                </span>
                <span className={styles.submitIcon}>
                  {editVideoIndex === -1 ? "+" : "✓"}
                </span>
              </button>

              {/* Video List with Reorder Buttons */}

              <div className={styles.videoList}>
                {videoLinks.map((video, index) => (
                  <div key={index} className={styles.sectionRow}>
                    {/* Up/Down Arrows */}
                    <div className={styles.reorderButtons}>
                      <button
                        className={styles.arrowButton}
                        onClick={() => handleMoveVideoUp(index)}
                        disabled={index === 0 || isSubmitting}
                        title="Move up"
                      >
                        ▲
                      </button>
                      <button
                        className={styles.arrowButton}
                        onClick={() => handleMoveVideoDown(index)}
                        disabled={
                          index === videoLinks.length - 1 || isSubmitting
                        }
                        title="Move down"
                      >
                        ▼
                      </button>
                    </div>

                    <span className={styles.sectionLabel}>
                      {video.title || "Untitled Video"}
                    </span>

                    <div className={styles.sectionActions}>
                      <button
                        className={styles.editIconButton}
                        onClick={() => handleEditVideo(index)}
                        disabled={isSubmitting}
                      >
                        ✎
                      </button>
                      <button
                        className={styles.deleteIconButton}
                        onClick={() => handleDeleteVideo(index)}
                        disabled={isSubmitting}
                      >
                        🗑
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <button
            className={`${styles.submitButton} ${
              isSubmitting ? styles.submitting : ""
            }`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span>
                  {editCommunityId === null ? "Creating..." : "Updating..."}
                </span>
                <span className={styles.submitIcon}>⟳</span>
              </>
            ) : (
              <>
                <span>Submit Community</span>
                <span className={styles.submitIcon}>＋</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateCommunityPage;
